import { z } from 'zod';
import { paginationQuerySchema, paginated } from './common.js';
/**
 * Driver administration.
 *
 * Mirrors the vehicle contract, because the lifecycle is the same shape:
 * tenant-scoped, archived rather than deleted, audited on every mutation.
 *
 * `licenceNo` follows the same D3 reservation rule as a vehicle registration,
 * for the same reason: an inbound provider event keyed to it must never resolve
 * to a different person.
 */
export const driverSortSchema = z.enum(['name', 'status', 'createdAt']);
/** Mirrors the Driver.status default; drivers are ACTIVE or INACTIVE. */
export const driverStatusSchema = z.enum(['ACTIVE', 'INACTIVE']);
export const driverListQuerySchema = paginationQuerySchema
    .extend({
    sort: driverSortSchema.default('name'),
    direction: z.enum(['asc', 'desc']).default('asc'),
    status: driverStatusSchema.optional(),
    search: z.string().trim().min(1).optional(),
    /** Archived drivers are hidden unless explicitly requested. */
    /*
     * `z.enum` rather than `z.coerce.boolean()` — risk R8. Coercion is
     * truthiness-based, so the STRING "false" arrives truthy and `?includeArchived=false`
     * did the opposite of what it says. Converted with `=== 'true'` in the
     * service rather than a `.transform`, which would make it a ZodEffects and
     * drop the parameter from the published OpenAPI spec.
     */
    includeArchived: z.enum(['true', 'false']).optional(),
})
    .strict();
const RESERVED_ATTRIBUTE_KEYS = [
    'tenantid',
    'id',
    'driverid',
    'deletedat',
    'createdat',
    'updatedat',
];
/**
 * Free-form driver attributes.
 *
 * Also the intended home for a provider's own driver identifier, stored on
 * first resolution so later inbound events match exactly rather than by name.
 */
export const driverAttributesSchema = z
    .record(z.unknown())
    .refine((v) => !Object.keys(v).some((k) => RESERVED_ATTRIBUTE_KEYS.includes(k.toLowerCase())), {
    message: 'attributes may not contain server-owned keys such as tenantId or id',
})
    .refine((v) => JSON.stringify(v).length <= 8_192, {
    message: 'attributes payload is too large (8KB limit)',
});
/**
 * `licenceNo` is optional, and unique within the tenant when present.
 *
 * Follows behaviour decision D3, as a vehicle registration does: the licence
 * stays RESERVED even once the driver is archived, enforced by a non-partial
 * unique index. A licence identifies a real person whose incidents and events
 * are retained indefinitely, so re-using it means restoring the archived driver
 * rather than creating a second one.
 *
 * NULL is exempt — Postgres treats NULLs as distinct — because not every fleet
 * records licence numbers and requiring one would block driver enrolment.
 */
/**
 * The tachograph card this driver carries, as the DEVICE sends it.
 *
 * ⚠️ **This is the only way a driver's card can be recorded, and until it
 * existed there was none.** `TelemetryIngestionService` resolves an inbound
 * `driverRef` against `Driver.tachoCardId` — so the column was read on every
 * batch and writable by nothing, which meant driver identity could never work
 * for a real customer however correct the ingestion was. Measured before this
 * landed: 1 of 68 drivers carried a card, and that one was set by hand.
 *
 * ⚠️ **Separate from `licenceNo`, and the reason is lifetime.** A licence
 * carries D3's reservation rule — an archived driver keeps it, enforced by a
 * non-partial unique index, because an inbound event keyed to it must never
 * resolve to a different person. A card EXPIRES and is REISSUED while the
 * licence does not, so its uniqueness is partial and a deleted driver's card
 * may be reused. Folding them into one field would either block a reissue or
 * break the reservation.
 */
const tachoCardIdSchema = z.string().trim().min(1).max(64);
export const createDriverRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    licenceNo: z.string().trim().min(1).max(60).optional(),
    tachoCardId: tachoCardIdSchema.optional(),
    status: driverStatusSchema.default('ACTIVE'),
    attributes: driverAttributesSchema.default({}),
})
    .strict();
export const updateDriverRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(120).optional(),
    // Explicitly nullable: clearing a licence is a real edit, distinct from
    // leaving it untouched, and `.strict()` means the two cannot be conflated.
    licenceNo: z.string().trim().min(1).max(60).nullable().optional(),
    /*
     * Nullable for the same reason as the licence, and it happens more often:
     * a card expires and is reissued, so clearing one while the replacement is
     * being enrolled is an ordinary act rather than an edge case.
     */
    tachoCardId: tachoCardIdSchema.nullable().optional(),
    status: driverStatusSchema.optional(),
    attributes: driverAttributesSchema.optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'At least one field must be provided' });
export const driverLifecycleRequestSchema = z
    .object({ note: z.string().trim().min(1).max(500).optional() })
    .strict();
export const driverListItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    licenceNo: z.string().nullable(),
    /*
     * ⚠️ Returned so a reader can see whether this driver's tachograph readings
     * can be attributed at all. Without it the drivers' hours card names nobody
     * and the screen gives no reason — the card is the link, and an absent one
     * is the commonest explanation for an empty per-driver figure.
     */
    tachoCardId: z.string().nullable(),
    status: z.string(),
    archived: z.boolean(),
    createdAt: z.string(),
});
export const driverListResponseSchema = paginated(driverListItemSchema);
export const driverDetailSchema = driverListItemSchema.extend({
    attributes: z.record(z.unknown()),
    counts: z.object({
        openIncidents: z.number().int(),
        totalIncidents: z.number().int(),
        events: z.number().int(),
    }),
});
//# sourceMappingURL=driver.js.map